import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Popover,
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Chip,
  Autocomplete,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionSuggestField from './DescriptionSuggestField';
import { format as formatDate } from 'date-fns';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { utcToDisplayTimezone, timezoneToUtc } from '../../utils/timezoneUtils';
import { useTheme } from '@mui/material/styles';

import { useTeamspace, useOptionsForTeamspace } from '../../context/TeamspaceProvider';
import { useAuth } from '../../context/AuthContext';
import { isRestrictedStaff } from '../../config/staffRestrictions';
import axios from 'axios';
import { API_BASE_URL, addMemberEmailToData } from '../../config/api';
import { buildTagDescription, buildTagSuggestions, syncDescriptionWithTags } from '../../utils/tagDescription';
import ConcurrentEditNotice from '../Presence/ConcurrentEditNotice';
import useIsPhone from '../../hooks/useIsPhone';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Helper functions to convert between minutes and HH:MM format
const minutesToHHMM = (minutes) => {
  if (!minutes || minutes === 0) return '00:00';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const HHMMToMinutes = (hhmm) => {
  if (!hhmm || hhmm === '') return 0;
  // Handle different input formats
  const parts = hhmm.split(':');
  if (parts.length === 2) {
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;
    return hours * 60 + minutes;
  }
  // Fallback: treat as minutes if no colon
  return parseInt(hhmm) || 0;
};

// Helper function to extract hours from HH:MM format
const getHoursFromHHMM = (hhmm) => {
  if (!hhmm || hhmm === '') return 0;
  const parts = hhmm.split(':');
  if (parts.length === 2) {
    return parseInt(parts[0]) || 0;
  }
  return 0;
};

const QuickTimeEntryForm = ({
  open,
  anchorEl,
  onClose,
  initialData = null, // For editing existing entries
  selectedTimeSlot = null, // For creating new entries { start, end }
  onSave,
  onDelete,
  groupEntries: groupEntriesProp = null,
  filterSelectedMember = null, // username/email of the currently filtered calendar member
}) => {
  const { selectedTeamspaceId: globalSelectedTeamspaceId, teamspaces, teamspaceIdToNameMap, refreshTimeEntries, userPreferences, displayTimezone, timezoneReady, startTimeEntryMutation, endTimeEntryMutation } = useTeamspace();
  const { user, role_dict } = useAuth();
  const theme = useTheme();
  // Phones: the popover fills the screen instead of hanging off the tapped slot.
  const isPhone = useIsPhone();

  // Form's teamspace (can differ from global when user changes selector in form)
  const [formTeamspaceId, setFormTeamspaceId] = useState(null);

  // Cached options for form's teamspace (loaded when teamspace initializes or when form teamspace changes)
  const formOptions = useOptionsForTeamspace(formTeamspaceId);
  const formProjects = formOptions.projects;
  const formMembers = formOptions.members;
  const formTags = formOptions.tags;
  const formCategories = formOptions.categories;
  const formMemberLookup = formOptions.memberLookup;
  const formTagLookup = formOptions.tagLookup;
  const formLookupLoading = formOptions.loading;

  // Determine if the current user is 'staff' in the selected teamspace
  const teamspaceName = teamspaceIdToNameMap[globalSelectedTeamspaceId];
  const isStaff = user && teamspaceName && role_dict[teamspaceName]?.role?.includes('Staff');
  // DCC/PH Staff may log time for other members too (2026-10-08) — only the
  // entries they can see stay limited to their own.
  const restrictedStaff = isRestrictedStaff(user, role_dict, teamspaceName);
  const canCreateForOthers = !!(user && teamspaceName && role_dict[teamspaceName]?.role?.some(r => ['Admin', 'Developer', 'Lead'].includes(r)))
    || restrictedStaff;
  const memberLocked = isStaff && !restrictedStaff;
  
  // Find current user's member ID for staff users
  const currentUserMember = useMemo(() => {
    if (!user || !formMembers.length) return null;
    return formMembers.find(member => 
      member.email === user.email || 
      member.username === user.username ||
      member.label === user.username ||
      member.label === user.email
    );
  }, [user, formMembers]);
  
  // Helper functions are now imported from utils/timezoneUtils.js
  // Wrapper function that uses displayTimezone from context for convenience
  const utcToDisplayTimezoneLocal = (utcDateString) => utcToDisplayTimezone(utcDateString, displayTimezone);
  

  // Options for the form's teamspace come from cache (useOptionsForTeamspace)
  const [error, setError] = useState(null);
  // Helper to round time to nearest 5 minutes
  const roundToNearestFiveMinutes = (time) => {
    return time.second(0);
  };

  // Get default_billable from user preferences, default to true if not set
  // This is available immediately when form opens, no need to wait for member loading
  const defaultBillable = userPreferences?.default_billable !== undefined ? userPreferences.default_billable : true;

  // Calculate initial values - use timer data if provided, otherwise use defaults
  const getInitialFormData = () => {
    // Get default_billable from user preferences, default to true if not set
    // If editing existing entry (initialData), use existing billable value
    const defaultBillable = initialData?.billable !== undefined 
      ? initialData.billable 
      : (userPreferences?.default_billable !== undefined ? userPreferences.default_billable : true);
    
    // Initialize timezone from displayTimezone
    const initialTimezone = displayTimezone || 'UTC';
    
    if (selectedTimeSlot && selectedTimeSlot.start && selectedTimeSlot.end) {
      // Calendar provides times already in display timezone (via FullCalendar's timeZone prop)
      // No conversion needed - use them directly
      const startDate = dayjs(selectedTimeSlot.start);
      const endDate = dayjs(selectedTimeSlot.end);
      const durationMinutes = Math.floor((endDate.diff(startDate, 'minute')));
      
      return {
        description: '',
        member: [],
        project: '',
        category: '',
        tag: [], // Changed from '' to []
        billable: defaultBillable,
        date: startDate,
        start_time: startDate,
        end_time: endDate,
        duration_minutes: durationMinutes,
        notes: '',
        timezone: initialTimezone,
        teamspace: formTeamspaceId
      };
    } else {
      // Default values for manual entry - use display timezone
      // If timezone is UTC, no conversion needed
      const initialDate = (!displayTimezone || displayTimezone === 'UTC')
        ? dayjs().utc()
        : dayjs().tz(displayTimezone);
      const initialStartTimeBase = (!displayTimezone || displayTimezone === 'UTC')
        ? dayjs().utc()
        : dayjs().tz(displayTimezone);
      const initialStartTime = roundToNearestFiveMinutes(initialStartTimeBase);
      const initialEndTime = initialDate
        .hour(initialStartTime.hour())
        .minute(initialStartTime.minute())
        .second(0)
        .add(15, 'minute');

        return {
          description: '',
          member: [],
          project: '',
          category: '',
          tag: [],
          billable: defaultBillable,
          date: initialDate,       
          start_time: initialStartTime,  
          end_time: initialEndTime,      
          duration_minutes: 15,
          notes: '',
          timezone: initialTimezone,
          teamspace: formTeamspaceId
        };
    }
  };

  const [formData, setFormData] = useState(getInitialFormData());
  const [validationErrors, setValidationErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [durationInputValue, setDurationInputValue] = useState(null); // Track raw input value
  const [durationError, setDurationError] = useState(''); // Track duration validation error
  const durationInputRef = useRef(null); // Ref for duration input to preserve cursor position
  const durationPrevValueRef = useRef(null); // Track previous value for cursor calculation
  const memberEntriesRef = useRef([]);
  const [expandedFields, setExpandedFields] = useState({
    member: false,
    project: false,
    tag: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showTeamspaceChangeDialog, setShowTeamspaceChangeDialog] = useState(false);
  const [pendingTeamspaceId, setPendingTeamspaceId] = useState(null);
  const [descriptionSuggestions, setDescriptionSuggestions] = useState([]);
  const [apiErrorDialogOpen, setApiErrorDialogOpen] = useState(false);
  const [duplicateCheckDialogOpen, setDuplicateCheckDialogOpen] = useState(false);
  const [duplicateCheckInfo, setDuplicateCheckInfo] = useState(null);
  const groupEntries = groupEntriesProp || [];
  const [groupConfirmMode, setGroupConfirmMode] = useState(null); // 'myself' | 'all' | null
  const [billableSetByProject, setBillableSetByProject] = useState(false);
  const [billableOverriddenFromProject, setBillableOverriddenFromProject] = useState(false);

  const isEdit = !!initialData;
  const isGroupEntry = isEdit && groupEntries.length > 1;

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAutocompleteChange = (name) => (event, value) => {
    if (name === 'tag') {
      // Handle multiple tags
      const tagIds = Array.isArray(value) ? value.map(v => v.id) : (value ? [value.id] : []);
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: tagIds,
        // Keep a tag-derived description in sync with the new tags; a
        // description the user typed themselves is left untouched.
        description: syncDescriptionWithTags({
          description: prevFormData.description,
          previousTagIds: prevFormData.tag,
          nextTagIds: tagIds,
          tagLookup: formTagLookup,
        }),
      }));
    } else if (name === 'project') {
      // Handle project/category selection - mutually exclusive
      if (value) {
        const defaultBillable = userPreferences?.default_billable !== undefined ? userPreferences.default_billable : true;
        if (value.isCategory) {
          // Selected a category - set category and clear project; category entries are never billable
          setFormData(prevFormData => ({
            ...prevFormData,
            category: value.id,
            project: '',
            billable: false,
          }));
          setBillableSetByProject(false);
          setBillableOverriddenFromProject(false);
        } else {
          // Selected a project - set project, clear category, apply project's billable
          setFormData(prevFormData => ({
            ...prevFormData,
            project: value.id,
            category: '',
            billable: value.billable !== undefined ? value.billable : defaultBillable,
          }));
          setBillableSetByProject(true);
          setBillableOverriddenFromProject(false);
        }
      } else {
        const defaultBillable = userPreferences?.default_billable !== undefined ? userPreferences.default_billable : true;
        // Cleared selection - clear both, revert billable to default
        setFormData(prevFormData => ({
          ...prevFormData,
          project: '',
          category: '',
          billable: defaultBillable,
        }));
        setBillableSetByProject(false);
        setBillableOverriddenFromProject(false);
      }
    } else if (name === 'member') {
      const memberIds = Array.isArray(value) ? value.map(v => String(v.id)) : (value ? [String(value.id)] : []);
      setFormData(prevFormData => ({
        ...prevFormData,
        member: memberIds,
      }));
    } else {
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: value ? value.id : ''
      }));
    }
  };

  const handleDateTimeChange = (name) => (newValue) => {
    if (name === 'start_time') {
      setFormData(prevFormData => {
        const updated = { ...prevFormData, start_time: newValue };
        // If end_time exists, recalculate duration
        if (updated.end_time && updated.end_time.isValid() && newValue && newValue.isValid()) {
          const startDateTime = updated.date
            .hour(newValue.hour())
            .minute(newValue.minute())
            .second(0);
          let endDateTime = updated.date
            .hour(updated.end_time.hour())
            .minute(updated.end_time.minute())
            .second(0);
          let diffMinutes = endDateTime.diff(startDateTime, 'minute');
          // Handle case where end_time is before start_time (next day)
          if (diffMinutes < 0) {
            endDateTime = endDateTime.add(1, 'day');
            diffMinutes = endDateTime.diff(startDateTime, 'minute');
          }
          updated.duration_minutes = Math.max(diffMinutes, 0);
        } else if (!updated.end_time || !updated.end_time.isValid()) {
          // If end_time doesn't exist, set duration to 0
          updated.duration_minutes = 0;
        }
        return updated;
      });
    } else if (name === 'end_time') {
      setFormData(prevFormData => {
        const updated = { ...prevFormData, end_time: newValue };
        // If end_time is set and start_time exists, calculate duration
        if (newValue && newValue.isValid() && updated.start_time && updated.start_time.isValid()) {
          const startDateTime = updated.date
            .hour(updated.start_time.hour())
            .minute(updated.start_time.minute())
            .second(0);
          let endDateTime = updated.date
            .hour(newValue.hour())
            .minute(newValue.minute())
            .second(0);
          let diffMinutes = endDateTime.diff(startDateTime, 'minute');
          if (diffMinutes < 0) {
            endDateTime = endDateTime.add(1, 'day');
            diffMinutes = endDateTime.diff(startDateTime, 'minute');
          }
          updated.duration_minutes = Math.max(diffMinutes, 0);
        } else if (!newValue) {
          // If end_time is cleared, don't change duration
        }
        return updated;
      });
    } else {
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: newValue
      }));
    }
  };

  // Handle duration change - update end_time
  const handleDurationChange = (inputValue) => {
    // If empty, allow it during typing but don't update formData yet
    if (inputValue === '' || inputValue.trim() === '') {
      setDurationInputValue('');
      return;
    }
    // HHMMToMinutes already handles minutes > 59 correctly (e.g., 01:80 = 140 minutes)
    const minutes = HHMMToMinutes(inputValue);
    const clampedValue = Math.min(Math.max(minutes, 0), 1440); // 24 hours max
    setDurationInputValue(null); // Clear raw input, use normalized value
    
    setFormData(prev => {
      const updated = { ...prev, duration_minutes: clampedValue };
      // If start_time exists, calculate end_time
      if (updated.start_time && updated.start_time.isValid() && clampedValue > 0) {
        const startDateTime = updated.date
          .hour(updated.start_time.hour())
          .minute(updated.start_time.minute())
          .second(0);
        updated.end_time = startDateTime.add(clampedValue, 'minute');
      } else {
        updated.end_time = null;
      }
      return updated;
    });
  };

  const toggleField = (field) => {
    setExpandedFields(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getSelectedLabel = (field, options, value) => {
    if (field === 'member') {
      if (!value || (Array.isArray(value) && value.length === 0)) return null;
      const memberIds = Array.isArray(value) ? value : [value];
      if (memberIds.length === 0) return null;
      if (memberIds.length === 1) {
        const option = options.find(o => String(o.id) === String(memberIds[0]));
        return option ? option.label : null;
      }
      return `(${memberIds.length}) members`;
    }
    if (field === 'tag' || field === 'tags') {
      // Handle array of tag IDs
      if (!value || (Array.isArray(value) && value.length === 0)) return null;
      const tagIds = Array.isArray(value) ? value : [value];
      if (tagIds.length === 0) return null;
      if (tagIds.length === 1) {
        // Convert both to strings for comparison to handle type mismatches
        const tagId = String(tagIds[0]);
        const option = options.find(o => String(o.id) === tagId);
        return option ? option.label : null;
      }
      return `(${tagIds.length} tags)`;
    }
    if (field === 'project') {
      // Handle project or category selection
      if (!value && !formData.category) return null;
      // Check category first
      if (formData.category) {
        const categoryOption = formCategories.find(c => String(c.id) === String(formData.category));
        if (categoryOption) return categoryOption.label;
      }
      // Then check project
      if (value) {
        const option = options.find(o => String(o.id) === String(value));
        return option ? option.label : null;
      }
      return null;
    }
    if (!value) return null;
    // Convert both to strings for comparison to handle type mismatches
    const option = options.find(o => String(o.id) === String(value));
    return option ? option.label : null;
  };

  const validateForm = () => {
    console.log("Validating form...", formData);
    const errors = {};
    // Description is optional
    if (!isStaff && formData.member.length === 0) {
      errors.member = 'Members is required.';
    }
    // Project is required in the UI even though time_entry.project is nullable.
    // The field is a single combined picker, so a time entry category satisfies it too.
    if (!formData.project && !formData.category) {
      errors.project = 'Project is required.';
    }
    
    // Validate date
    if (!formData.date || !formData.date.isValid()) {
      errors.date = 'Date is required and must be valid.';
    }
    // Validate start time
    if (!formData.start_time || !formData.start_time.isValid()) {
      errors.start_time = 'Start time is required and must be valid.';
    }
    // Validate duration - optional, but if provided must be valid
    // Also check if either duration or end_time is provided
    const hasDuration = formData.duration_minutes && formData.duration_minutes > 0;
    const hasEndTime = formData.end_time && formData.end_time.isValid();
    
    if (!hasDuration && !hasEndTime) {
      // Both are optional, so no error
    } else if (formData.duration_minutes && formData.duration_minutes < 0) {
      errors.duration_minutes = 'Duration cannot be negative.';
    }
    setValidationErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    console.log("Validation errors:", errors);
    console.log("Form is valid:", isValid);
    return isValid;
  };

  // Sync formData.member when group membership changes (provided by parent via prop)
  useEffect(() => {
    if (groupEntries.length > 1) {
      setFormData(prev => ({ ...prev, member: groupEntries.map(e => String(e.member)) }));
    }
  }, [groupEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tag names offered alongside the recently-used descriptions, selected tags first
  const tagSuggestions = useMemo(
    () => buildTagSuggestions({ selectedTagIds: formData.tag, tagLookup: formTagLookup }),
    [formData.tag, formTagLookup]
  );

  // Helper: compute suggestions from raw entries based on project/category
  const computeDescriptionSuggestions = (entries, projectId, categoryId, memberId) => {
    const memberEntries = entries.filter(e => String(e.member) === String(memberId));
    let filtered = memberEntries;
    if (projectId || categoryId) {
      const projectFiltered = memberEntries.filter(e =>
        projectId ? String(e.project) === String(projectId) : String(e.category) === String(categoryId)
      );
      if (projectFiltered.length > 0) filtered = projectFiltered;
    }
    const seen = new Set();
    const result = [];
    for (const entry of filtered) {
      const desc = entry.description?.trim();
      if (desc && !seen.has(desc)) { seen.add(desc); result.push(desc); }
    }
    return result.slice(0, 20);
  };

  // Fetch recent entries for description suggestions when form opens or teamspace/member changes
  useEffect(() => {
    memberEntriesRef.current = [];
    setDescriptionSuggestions([]);
    if (!open || !formTeamspaceId || !currentUserMember?.id) return;
    const fetchDescriptions = async () => {
      try {
        const params = new URLSearchParams({ page_size: 100, member: currentUserMember.id, is_optimized: 'true' });
        const response = await axios.get(
          `${API_BASE_URL}teamspaces/${formTeamspaceId}/time_entries/?${params.toString()}`
        );
        const entries = Array.isArray(response.data) ? response.data : (response.data.results || []);
        memberEntriesRef.current = entries;
        setDescriptionSuggestions(
          computeDescriptionSuggestions(entries, formData.project, formData.category, currentUserMember.id)
        );
      } catch (err) {
        console.warn('Failed to fetch description suggestions:', err);
      }
    };
    fetchDescriptions();
  }, [open, formTeamspaceId, currentUserMember]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recompute suggestions when project/category selection changes
  useEffect(() => {
    if (!currentUserMember?.id || !memberEntriesRef.current.length) return;
    setDescriptionSuggestions(
      computeDescriptionSuggestions(memberEntriesRef.current, formData.project, formData.category, currentUserMember.id)
    );
  }, [formData.project, formData.category]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkForDuplicates = async (memberIds, startDateUTC, endDateUTC, basePayload) => {
    const params = new URLSearchParams({ start_date: startDateUTC, end_date: startDateUTC, timezone: 'UTC', page_size: 200 });
    const response = await axios.get(`${API_BASE_URL}teamspaces/${formTeamspaceId}/time_entries/?${params.toString()}`);
    const entries = Array.isArray(response.data) ? response.data : (response.data.results || []);
    const targetEndMs = endDateUTC ? new Date(endDateUTC).getTime() : null;
    return memberIds.filter(memberId =>
      entries.some(entry => {
        if (String(entry.member) !== String(memberId)) return false;
        const entryEndMs = entry.end_time ? new Date(entry.end_time).getTime() : null;
        if (entryEndMs !== targetEndMs) return false;
        if (basePayload.project) { if (String(entry.project) !== String(basePayload.project)) return false; } else if (entry.project) return false;
        if (basePayload.category) { if (String(entry.category) !== String(basePayload.category)) return false; } else if (entry.category) return false;
        if ((entry.description || '') !== (basePayload.description || '')) return false;
        return true;
      })
    );
  };

  const doPerformSave = async (memberIds, basePayload, isEdit, groupMemberChanges = null) => {
    startTimeEntryMutation();
    setIsSubmitting(true);
    try {
      const primaryMemberId = memberIds[0] || '';
      const additionalMemberIds = memberIds.slice(1);

      if (onSave) {
        const primaryPayload = addMemberEmailToData({ ...basePayload, member: primaryMemberId, teamspace: formTeamspaceId }, user);
        if (isEdit) {
          await onSave(initialData.id, primaryPayload);
        } else {
          await onSave(primaryPayload);
        }

        for (const memberId of additionalMemberIds) {
          const additionalPayload = addMemberEmailToData({ ...basePayload, member: memberId, teamspace: formTeamspaceId }, user);
          await onSave(additionalPayload);
        }
      }

      if (groupMemberChanges) {
        const { addedIds, removedIds } = groupMemberChanges;
        // Use the group's existing data for added members so the new entry matches the rest of the group.
        const referenceEntry = groupEntries.find(e => String(e.id) !== String(initialData?.id)) || groupEntries[0];
        const addedMemberBasePayload = referenceEntry
          ? {
              description: referenceEntry.description || '',
              billable: Boolean(referenceEntry.billable),
              start_time: referenceEntry.start_time,
              end_time: referenceEntry.end_time,
              notes: referenceEntry.notes || '',
              project: referenceEntry.project || null,
              category: referenceEntry.category || null,
              tag: Array.isArray(referenceEntry.tag) ? referenceEntry.tag : [],
            }
          : basePayload;
        for (const memberId of addedIds) {
          const payload = addMemberEmailToData({ ...addedMemberBasePayload, member: memberId, teamspace: formTeamspaceId }, user);
          await axios.post(`${API_BASE_URL}teamspaces/${formTeamspaceId}/time_entries/`, payload);
        }
        for (const memberId of removedIds) {
          const entryToDelete = groupEntries.find(e => String(e.member) === memberId);
          if (entryToDelete && String(entryToDelete.id) !== String(initialData?.id)) {
            const deleteUrl = currentUserMember?.id
              ? `${API_BASE_URL}teamspaces/${formTeamspaceId}/time_entries/${entryToDelete.id}/?action_by=${currentUserMember.id}`
              : `${API_BASE_URL}teamspaces/${formTeamspaceId}/time_entries/${entryToDelete.id}/`;
            await axios.delete(deleteUrl);
          }
        }
        if (refreshTimeEntries) refreshTimeEntries();
      }

      setSuccessMessage('Time entry saved successfully');
      const usernameToMatch = user?.username || user?.email;
      const currentMemberForReset = memberOptions.find(m => m.label === usernameToMatch);
      const now = (displayTimezone && displayTimezone !== 'UTC') ? dayjs().tz(displayTimezone) : dayjs().utc();
      const resetStartTime = roundToNearestFiveMinutes(now);
      const resetEndTime = now
        .hour(resetStartTime.hour())
        .minute(resetStartTime.minute())
        .second(0)
        .add(15, 'minute');
      setFormData({
        description: '',
        member: currentMemberForReset ? [String(currentMemberForReset.id)] : (currentUserMember ? [String(currentUserMember.id)] : []),
        project: '',
        category: '',
        tag: [],
        billable: defaultBillable,
        date: now,
        start_time: resetStartTime,
        end_time: resetEndTime,
        duration_minutes: 15,
        notes: '',
        timezone: displayTimezone || 'UTC',
        teamspace: null
      });
      setValidationErrors({});
    } catch (err) {
      console.error('Error saving time entry:', err);
      const isNetworkError = !err.response && (
        err.message?.includes('Network Error') ||
        err.code === 'ERR_CONNECTION_REFUSED' ||
        err.code === 'ERR_NETWORK' ||
        err.message?.includes('ERR_CONNECTION_REFUSED')
      );
      const responseStatus = err.response?.status;
      if (isNetworkError || (responseStatus && responseStatus !== 200 && responseStatus !== 201)) {
        setApiErrorDialogOpen(true);
        return;
      }
      let errorMessage = err.message;
      if (err.response?.data) {
        if (typeof err.response.data === 'string') errorMessage = err.response.data;
        else if (err.response.data.error) errorMessage = err.response.data.error;
        else if (err.response.data.detail) errorMessage = err.response.data.detail;
        else if (err.response.data.member) errorMessage = `Member error: ${Array.isArray(err.response.data.member) ? err.response.data.member.join(', ') : err.response.data.member}`;
        else errorMessage = JSON.stringify(err.response.data);
      }
      setError(errorMessage);
    } finally {
      endTimeEntryMutation();
      setIsSubmitting(false);
    }
  };

  const doSaveForAll = async (basePayload) => {
    startTimeEntryMutation();
    setIsSubmitting(true);
    try {
      const existingMemberIds = groupEntries.map(e => String(e.member));
      const currentMemberIds = formData.member.map(String);
      const addedIds = currentMemberIds.filter(id => !existingMemberIds.includes(id));
      const removedIds = existingMemberIds.filter(id => !currentMemberIds.includes(id));

      // Use direct axios.put for all group entries to avoid onSave's side-effect of closing the form.
      for (const groupEntry of groupEntries) {
        if (removedIds.includes(String(groupEntry.member))) continue;
        const payload = addMemberEmailToData({ ...basePayload, member: String(groupEntry.member), teamspace: formTeamspaceId }, user);
        await axios.put(`${API_BASE_URL}teamspaces/${formTeamspaceId}/time_entries/${groupEntry.id}/`, payload);
      }

      for (const memberId of addedIds) {
        const payload = addMemberEmailToData({ ...basePayload, member: memberId, teamspace: formTeamspaceId }, user);
        await axios.post(`${API_BASE_URL}teamspaces/${formTeamspaceId}/time_entries/`, payload);
      }

      for (const memberId of removedIds) {
        const entryToDelete = groupEntries.find(e => String(e.member) === memberId);
        if (entryToDelete) {
          const deleteUrl = currentUserMember?.id
            ? `${API_BASE_URL}teamspaces/${formTeamspaceId}/time_entries/${entryToDelete.id}/?action_by=${currentUserMember.id}`
            : `${API_BASE_URL}teamspaces/${formTeamspaceId}/time_entries/${entryToDelete.id}/`;
          await axios.delete(deleteUrl);
        }
      }

      if (refreshTimeEntries) refreshTimeEntries();
      onClose();
    } catch (err) {
      console.error('Error saving for all members:', err);
      setError(err.message);
    } finally {
      endTimeEntryMutation();
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    console.log("Submit button clicked. Attempting to validate form...");
    if (validateForm()) {
      await performSubmit('myself');
    }
  };

  const performSubmit = async (scope = 'default') => {
    console.log("Form is valid. Attempting to submit...");
    const formTimezone = formData.timezone || displayTimezone || 'UTC';

    const dateTimeStr = `${formData.date.format('YYYY-MM-DD')}T${formData.start_time.format('HH:mm:ss')}`;
    const startDateTime = (!formTimezone || formTimezone === 'UTC')
      ? dayjs.utc(dateTimeStr)
      : dayjs.tz(dateTimeStr, formTimezone);
    const startDateUTC = timezoneToUtc(startDateTime, formTimezone);

    let endDateUTC;
    if (formData.end_time && formData.end_time.isValid()) {
      const endDateTimeStr = `${formData.date.format('YYYY-MM-DD')}T${formData.end_time.format('HH:mm:ss')}`;
      let endDateTime = (!formTimezone || formTimezone === 'UTC')
        ? dayjs.utc(endDateTimeStr)
        : dayjs.tz(endDateTimeStr, formTimezone);
      if (endDateTime.isBefore(startDateTime)) endDateTime = endDateTime.add(1, 'day');
      endDateUTC = timezoneToUtc(endDateTime, formTimezone);
    } else if (formData.duration_minutes && formData.duration_minutes > 0) {
      const startDate = new Date(startDateUTC);
      endDateUTC = new Date(startDate.getTime() + (formData.duration_minutes * 60 * 1000)).toISOString();
    } else {
      throw new Error('Either duration or end time must be provided.');
    }

    const memberIds = formData.member.length > 0
      ? formData.member
      : (isStaff && currentUserMember ? [String(currentUserMember.id)] : []);

    const trimmedDescription = formData.description?.trim();
    let description = formData.description || '';
    if (!trimmedDescription) {
      // Use all selected tag names as description if description is empty
      description = buildTagDescription(formData.tag, formTagLookup) || description;
    }

    const projectExists = formData.project && projectAndCategoryOptions.some(o => String(o.id) === String(formData.project) && !o.isCategory);
    const categoryExists = formData.category && projectAndCategoryOptions.some(o => String(o.id) === String(formData.category) && o.isCategory);
    const validTagIds = formData.tag && Array.isArray(formData.tag)
      ? formData.tag.filter(id => tagOptions.some(o => String(o.id) === String(id)))
      : [];

    const basePayload = {
      description,
      billable: Boolean(formData.billable) && !formData.category,
      start_time: startDateUTC,
      end_time: endDateUTC,
      notes: formData.notes || '',
      created_by: currentUserMember?.id,
      lastedited_by: currentUserMember?.id,
    };

    if (initialData) {
      basePayload.project = projectExists ? parseInt(formData.project, 10) : null;
      basePayload.category = categoryExists ? formData.category : null;
      if (projectExists) basePayload.category = null;
      if (categoryExists) basePayload.project = null;
      basePayload.tag = validTagIds.length > 0 ? validTagIds.map(id => parseInt(id, 10)) : [];
    } else {
      if (categoryExists) basePayload.category = formData.category;
      else if (projectExists) basePayload.project = parseInt(formData.project, 10);
      if (validTagIds.length > 0) basePayload.tag = validTagIds.map(id => parseInt(id, 10));
    }

    // For group "save for all", dispatch to doSaveForAll and skip duplicate check
    if (isGroupEntry && scope === 'all') {
      await doSaveForAll(basePayload);
      return;
    }

    // For group "save for myself", only save for the entry being edited (initialData.member).
    // For non-group edits, ensure initialData.member is first so it gets updated (not re-created).
    const resolvedMemberIds = (isGroupEntry && scope === 'myself')
      ? [String(initialData.member)]
      : (isEdit && initialData?.member)
        ? [String(initialData.member), ...memberIds.filter(id => String(id) !== String(initialData.member))]
        : memberIds;

    // For create: check all members. For edit: check only additional members (primary is being edited).
    // Skip duplicate check entirely for group "save for myself" (updating existing entry).
    const membersToCheckForDupes = (isGroupEntry && scope === 'myself')
      ? []
      : (isEdit ? resolvedMemberIds.slice(1) : resolvedMemberIds);

    if (membersToCheckForDupes.length > 0) {
      try {
        const duplicateIds = await checkForDuplicates(membersToCheckForDupes, startDateUTC, endDateUTC, basePayload);
        if (duplicateIds.length > 0) {
          setDuplicateCheckInfo({ duplicateIds, memberIds: resolvedMemberIds, basePayload, isEdit });
          setDuplicateCheckDialogOpen(true);
          return;
        }
      } catch (err) {
        console.error('Duplicate check failed, proceeding anyway:', err);
      }
    }

    const groupMemberChanges = (isGroupEntry && scope === 'myself') ? {
      addedIds: formData.member.filter(id => !groupEntries.map(e => String(e.member)).includes(String(id))),
      removedIds: groupEntries.map(e => String(e.member)).filter(id => !formData.member.includes(id)),
    } : null;

    await doPerformSave(resolvedMemberIds, basePayload, isEdit, groupMemberChanges);
  };

  // Convert lookup maps to array of options for Autocomplete components
  const availableProjects = useMemo(() => {
    return formProjects
      .filter(p => !p.is_archived) // Exclude archived projects
      .map(p => ({ id: p.id, label: p.full_project_name || p.name || '', isCategory: false, billable: p.billable }))
      .sort((a, b) => {
        const labelA = (a.label || '').toLowerCase();
        const labelB = (b.label || '').toLowerCase();
        return labelA.localeCompare(labelB);
      });
  }, [formProjects]);
  
  // Combine categories (first) and projects for the project dropdown
  const projectAndCategoryOptions = useMemo(() => {
    // Categories first, then projects
    return [...formCategories, ...availableProjects];
  }, [formCategories, availableProjects]);
  
  const memberOptions = useMemo(() => Object.entries(formMemberLookup).map(([id, label]) => ({ id, label })), [formMemberLookup]);
  const tagOptions = useMemo(() => Object.entries(formTagLookup).map(([id, label]) => ({ id, label })), [formTagLookup]);
  

  // Initialize form teamspace when dialog opens.
  // IMPORTANT: When editing, always use the entry's own teamspace (not the current/global teamspace),
  // otherwise project/member/tag lookups will be loaded for the wrong teamspace and saving can fail.
  const prevOpenRefForTeamspace = useRef(open);
  useEffect(() => {
    const isOpening = open && !prevOpenRefForTeamspace.current;
    if (isOpening) {
      const entryTeamspaceId = initialData?.teamspace ?? initialData?.teamspace_id ?? null;
      const preferredTeamspaceId = entryTeamspaceId ?? globalSelectedTeamspaceId ?? null;
      if (preferredTeamspaceId !== null && preferredTeamspaceId !== undefined && preferredTeamspaceId !== '') {
        const normalized = Number(preferredTeamspaceId);
        if (!Number.isNaN(normalized)) {
          setFormTeamspaceId(normalized);
        }
      }
    }
    prevOpenRefForTeamspace.current = open;
  }, [open, globalSelectedTeamspaceId, initialData?.teamspace, initialData?.teamspace_id]);

  // While editing, ensure the form stays locked to the entry's teamspace even if the global teamspace changes.
  useEffect(() => {
    if (!open || !initialData) return;
    const entryTeamspaceId = initialData?.teamspace ?? initialData?.teamspace_id ?? null;
    if (entryTeamspaceId === null || entryTeamspaceId === undefined || entryTeamspaceId === '') return;
    const normalized = Number(entryTeamspaceId);
    if (Number.isNaN(normalized)) return;
    if (Number(formTeamspaceId) !== normalized) {
      setFormTeamspaceId(normalized);
    }
  }, [open, initialData?.id, initialData?.teamspace, initialData?.teamspace_id, formTeamspaceId]);

  // Track if we've already initialized times for the current initialData to prevent recalculation
  const initializedDataIdRef = useRef(null);
  const initializedTimezoneRef = useRef(null); // Track timezone used during initialization
  const prevOpenRef = useRef(open); // Track previous open state to detect when form opens

  // Effect to reset form to defaults when modal opens or when initialData changes
  useEffect(() => {
    // Only reset form when opening (open changes from false to true) or when initialData changes
    // Don't reset when formTeamspaceId changes while form is already open
    const isOpening = open && !prevOpenRef.current;
    const shouldReset = isOpening || (open && initialData && initializedDataIdRef.current !== initialData.id);
    
    if (open && shouldReset) {
      const usernameToMatch = user?.username || user?.email;
      const currentMember = memberOptions.find(m => m.label === usernameToMatch);
      
      let formDataToSet;
      
      // If initialData exists, we're editing an existing entry
      if (initialData) {
        // Check if we need to initialize times:
        // 1. Entry ID changed (new entry)
        // 2. Timezone changed (members loaded, timezone became available)
        const entryChanged = initializedDataIdRef.current !== initialData.id;
        const timezoneChanged = initializedTimezoneRef.current !== displayTimezone;
        const shouldInitializeTimes = entryChanged || (timezoneChanged && initializedDataIdRef.current === initialData.id);
        
        if (shouldInitializeTimes) {
          // Convert UTC times to display timezone for form display
          // CalendarComponent passes original UTC times, so we convert once here
          const startTime = initialData.start_time 
            ? utcToDisplayTimezoneLocal(initialData.start_time) 
            : ((!displayTimezone || displayTimezone === 'UTC') ? dayjs().utc() : dayjs().tz(displayTimezone));
          
          let calculatedEndTime = null;
          let calculatedDurationMinutes = 15; // Default duration
          
          if (initialData.end_time) {
            // Convert UTC end time to display timezone
            calculatedEndTime = utcToDisplayTimezoneLocal(initialData.end_time);
            // Calculate duration from start_time and end_time to ensure accuracy
            if (startTime.isValid() && calculatedEndTime.isValid()) {
              let diffMinutes = calculatedEndTime.diff(startTime, 'minute');
              // Handle case where end_time is on the next day
              if (diffMinutes < 0) {
                diffMinutes = calculatedEndTime.add(1, 'day').diff(startTime, 'minute');
              }
              calculatedDurationMinutes = Math.max(diffMinutes, 0);
            }
          } else if (initialData.start_time) {
            // Calculate end time from start time and duration (fallback if end_time not available)
            const durationMinutes = initialData.duration ? Math.round(initialData.duration / 60) : 15;
            calculatedEndTime = startTime.add(durationMinutes, 'minute');
            calculatedDurationMinutes = durationMinutes;
          }
          
          console.log('📅 [QuickTimeEntryForm] Initializing form with UTC times (single conversion to display timezone):', {
            entryId: initialData.id,
            originalStartTimeUTC: initialData.start_time,
            convertedStartTime: startTime.format('YYYY-MM-DD HH:mm:ss'),
            originalEndTimeUTC: initialData.end_time,
            convertedEndTime: calculatedEndTime?.format('YYYY-MM-DD HH:mm:ss'),
            calculatedDurationMinutes: calculatedDurationMinutes,
            storedDurationMinutes: initialData.duration ? Math.round(initialData.duration / 60) : null,
            displayTimezone: displayTimezone,
            previousTimezone: initializedTimezoneRef.current,
            timezoneChanged: timezoneChanged,
            note: timezoneChanged ? 'Recalculating times because timezone changed (members loaded)' : 'Converting UTC times once to display timezone for form'
          });
        
        // Ensure mutual exclusivity: if category is provided, project should be empty, and vice versa
        // Priority: if category exists, use it and clear project; otherwise use project and clear category
        const hasCategory = initialData.category && initialData.category !== '' && initialData.category !== null;
        const hasProject = initialData.project && initialData.project !== '' && initialData.project !== null;
        
        formDataToSet = {
          description: initialData.description || '',
          member: initialData.member ? [String(initialData.member)] : [],
          project: hasCategory ? '' : (hasProject ? initialData.project : ''),
          category: hasProject ? '' : (hasCategory ? initialData.category : ''),
          tag: initialData.tag !== null && initialData.tag !== undefined 
            ? (Array.isArray(initialData.tag) 
                ? initialData.tag.map(id => String(id)).filter(id => id !== 'undefined' && id !== 'null')
                : [String(initialData.tag)].filter(id => id !== 'undefined' && id !== 'null'))
            : [],
          billable: initialData.billable ?? false,
          date: startTime,
          start_time: startTime,
          end_time: calculatedEndTime || startTime.add(15, 'minute'),
          duration_minutes: calculatedDurationMinutes, // Use calculated duration instead of stored duration
          notes: initialData.notes || '',
          timezone: displayTimezone || 'UTC',
          teamspace: (initialData.teamspace ?? initialData.teamspace_id ?? formTeamspaceId)
        };
          
          // Mark this entry as initialized with current timezone
          initializedDataIdRef.current = initialData.id;
          initializedTimezoneRef.current = displayTimezone;
        } else {
          // Times already initialized - only update non-time fields that might have changed
          console.log('📅 [QuickTimeEntryForm] Skipping time recalculation for entry (already initialized):', {
            entryId: initialData.id,
            note: 'Only updating non-time fields, times remain unchanged'
          });
          
          // Ensure mutual exclusivity: if category is provided, project should be empty, and vice versa
          // Priority: if category exists, use it and clear project; otherwise use project and clear category
          const hasCategory = initialData.category && initialData.category !== '' && initialData.category !== null;
          const hasProject = initialData.project && initialData.project !== '' && initialData.project !== null;
          
          formDataToSet = {
            ...formData,
            description: initialData.description || '',
            member: initialData.member ? [String(initialData.member)] : (formData.member.length > 0 ? formData.member : []),
            project: hasCategory ? '' : (hasProject ? initialData.project : (formData.project || '')),
            category: hasProject ? '' : (hasCategory ? initialData.category : (formData.category || '')),
            tag: initialData.tag !== null && initialData.tag !== undefined 
              ? (Array.isArray(initialData.tag) 
                  ? initialData.tag.map(id => String(id)).filter(id => id !== 'undefined' && id !== 'null')
                  : [String(initialData.tag)].filter(id => id !== 'undefined' && id !== 'null'))
              : formData.tag,
            billable: initialData.billable ?? formData.billable,
            notes: initialData.notes || '',
            timezone: formData.timezone || displayTimezone || 'UTC',
            teamspace: (initialData.teamspace ?? initialData.teamspace_id ?? formTeamspaceId)
            // Keep existing date, start_time, end_time, duration_minutes unchanged
          };
        }
      } else if (selectedTimeSlot && selectedTimeSlot.start && selectedTimeSlot.end) {
        // FullCalendar with timeZone returns Date objects where UTC time represents the local time in that timezone
        // We need to interpret these dates as being in displayTimezone
        // Extract UTC components and create dayjs objects in displayTimezone
        const startDateUTC = new Date(selectedTimeSlot.start);
        const endDateUTC = new Date(selectedTimeSlot.end);
        
        // Create dayjs objects in displayTimezone using the UTC components
        // FullCalendar's Date objects have UTC time that represents the display timezone time
        // IMPORTANT: If timezone is UTC, no conversion needed
        const startDateStr = `${startDateUTC.getUTCFullYear()}-${String(startDateUTC.getUTCMonth() + 1).padStart(2, '0')}-${String(startDateUTC.getUTCDate()).padStart(2, '0')}T${String(startDateUTC.getUTCHours()).padStart(2, '0')}:${String(startDateUTC.getUTCMinutes()).padStart(2, '0')}:${String(startDateUTC.getUTCSeconds()).padStart(2, '0')}`;
        const startDate = (!displayTimezone || displayTimezone === 'UTC')
          ? dayjs.utc(startDateStr)
          : dayjs.tz(startDateStr, displayTimezone);
        const endDateStr = `${endDateUTC.getUTCFullYear()}-${String(endDateUTC.getUTCMonth() + 1).padStart(2, '0')}-${String(endDateUTC.getUTCDate()).padStart(2, '0')}T${String(endDateUTC.getUTCHours()).padStart(2, '0')}:${String(endDateUTC.getUTCMinutes()).padStart(2, '0')}:${String(endDateUTC.getUTCSeconds()).padStart(2, '0')}`;
        const endDate = (!displayTimezone || displayTimezone === 'UTC')
          ? dayjs.utc(endDateStr)
          : dayjs.tz(endDateStr, displayTimezone);
        
        const durationMinutes = Math.floor((endDate.diff(startDate, 'minute')));
        
        console.log('📅 [QuickTimeEntryForm] Calendar time slot selected:', {
          selectedTimeSlotStart: selectedTimeSlot.start,
          selectedTimeSlotEnd: selectedTimeSlot.end,
          startDateUTCComponents: {
            year: startDateUTC.getUTCFullYear(),
            month: startDateUTC.getUTCMonth() + 1,
            day: startDateUTC.getUTCDate(),
            hours: startDateUTC.getUTCHours(),
            minutes: startDateUTC.getUTCMinutes()
          },
          parsedStartDate: startDate.format('YYYY-MM-DD HH:mm:ss'),
          parsedEndDate: endDate.format('YYYY-MM-DD HH:mm:ss'),
          displayTimezone: displayTimezone,
          note: 'Interpreting FullCalendar Date objects as displayTimezone time'
        });
        
        // Resolve default member:
        // - Admin/Developer/Lead viewing another member's calendar → pre-fill with that member
        // - Otherwise fall back to the logged-in user (Staff always uses their own)
        const filterMember = canCreateForOthers && filterSelectedMember &&
          filterSelectedMember !== user?.username && filterSelectedMember !== user?.email
          ? memberOptions.find(m => m.label === filterSelectedMember || m.member_username === filterSelectedMember)
          : null;
        const resolvedSelfId = currentMember?.id ?? currentUserMember?.id ?? null;
        const staffMember = !canCreateForOthers && isStaff && currentUserMember ? currentUserMember.id : null;
        const nonStaffMember = !canCreateForOthers && !isStaff ? resolvedSelfId : null;
        const selfMember = canCreateForOthers ? resolvedSelfId : null;
        const defaultMember = filterMember?.id || staffMember || nonStaffMember || selfMember;

        formDataToSet = {
          description: '',
          member: defaultMember ? [String(defaultMember)] : [],
          project: '',
          category: '',
          tag: [],
          billable: defaultBillable,  // Use user preference
          date: startDate,
          start_time: startDate,
          end_time: endDate,
          duration_minutes: durationMinutes,
          notes: '',
          timezone: displayTimezone || 'UTC',
          teamspace: formTeamspaceId
        };

        // Reset initialized refs for new entries
        initializedDataIdRef.current = null;
        initializedTimezoneRef.current = null;
      } else {
        // Default values for manual entry
        const now = (!displayTimezone || displayTimezone === 'UTC')
          ? dayjs().utc()
          : dayjs().tz(displayTimezone);
        const initialStartTime = roundToNearestFiveMinutes(now);
        const initialEndTime = now
          .hour(initialStartTime.hour())
          .minute(initialStartTime.minute())
          .second(0)
          .add(15, 'minute');

        // Resolve default member (same logic as above)
        const filterMember = canCreateForOthers && filterSelectedMember &&
          filterSelectedMember !== user?.username && filterSelectedMember !== user?.email
          ? memberOptions.find(m => m.label === filterSelectedMember || m.member_username === filterSelectedMember)
          : null;
        const resolvedSelfId = currentMember?.id ?? currentUserMember?.id ?? null;
        const staffMember = !canCreateForOthers && isStaff && currentUserMember ? currentUserMember.id : null;
        const nonStaffMember = !canCreateForOthers && !isStaff ? resolvedSelfId : null;
        const selfMember = canCreateForOthers ? resolvedSelfId : null;
        const defaultMember = filterMember?.id || staffMember || nonStaffMember || selfMember;

        formDataToSet = {
          description: '',
          member: defaultMember ? [String(defaultMember)] : [],
          project: '',
          category: '',
          tag: [],
          billable: defaultBillable,  // Use user preference
          date: now,
          start_time: initialStartTime,
          end_time: initialEndTime,
          duration_minutes: 15,
          notes: '',
          timezone: displayTimezone || 'UTC',
          teamspace: formTeamspaceId
        };
        
        // Reset initialized refs for new entries
        initializedDataIdRef.current = null;
        initializedTimezoneRef.current = null;
      }
      
      // For staff users editing, also ensure member is set if it wasn't set in initialData
      if (initialData && isStaff && formDataToSet.member.length === 0 && currentUserMember) {
        formDataToSet.member = [String(currentUserMember.id)];
      }
      
      setFormData(formDataToSet);
      setValidationErrors({});
      setError(null);
      setSuccessMessage('');
      // Set billableSetByProject if editing an existing entry whose billable matches the project's billable
      if (initialData?.project && formProjects.length > 0) {
        const matchingProject = formProjects.find(p => String(p.id) === String(initialData.project));
        if (matchingProject && initialData.billable === matchingProject.billable) {
          setBillableSetByProject(true);
        } else {
          setBillableSetByProject(false);
        }
      } else {
        setBillableSetByProject(false);
      }
      setBillableOverriddenFromProject(false);
      setDurationInputValue(null); // Reset duration input value
      setDurationError(''); // Reset duration error
      durationPrevValueRef.current = null; // Reset previous value ref
      // For staff users, automatically expand member field to show it's filled and locked
      setExpandedFields({ member: false, project: false, tag: false });
    } else {
      // Form closed - reset initialized refs
      initializedDataIdRef.current = null;
      initializedTimezoneRef.current = null;
    }
    
    // Update prevOpenRef to track open state
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, memberOptions, isStaff, selectedTimeSlot, initialData, currentUserMember, displayTimezone, formProjects]); // Removed formTeamspaceId - teamspace changes handled separately
  
  // Reset initialized ref when initialData changes to a different entry
  useEffect(() => {
    if (initialData && initializedDataIdRef.current !== initialData.id) {
      initializedDataIdRef.current = null;
      initializedTimezoneRef.current = null;
    }
  }, [initialData?.id]);

  // Effect to clear member, project, and tag when form teamspace changes
  useEffect(() => {
    // Only reset if teamspace actually changed (not on initial mount or dialog open)
    if (open && formTeamspaceId && prevFormTeamspaceIdRef.current !== formTeamspaceId && prevFormTeamspaceIdRef.current !== null) {
      // Clear member, project, category, and tag fields immediately
      setFormData(prev => ({
        ...prev,
        member: [], // Clear member field - will be set to NEW teamspace's member once loaded
        project: '',
        category: '',
        tag: [],
        teamspace: formTeamspaceId,
        // Keep all other fields unchanged: description, billable, date, start_time, end_time, duration_minutes
      }));
      setExpandedFields({ member: false, project: false, tag: false });
      setError(null); // Clear any previous errors
      setValidationErrors({}); // Clear validation errors
    }
    // Update ref to track current teamspace
    prevFormTeamspaceIdRef.current = formTeamspaceId;
  }, [formTeamspaceId, open]);

  // Track previous teamspace to detect changes
  const prevFormTeamspaceIdRef = useRef(formTeamspaceId);

  // Track if we've already updated form data after data loaded for this entry
  const dataLoadedUpdateRef = useRef({ entryId: null, teamspaceId: null });

  // Effect to update form data when form-specific data finishes loading
  // This ensures project and tag fields are properly populated when opening entries from different teamspaces
  useEffect(() => {
    // Only update if:
    // 1. Form is open
    // 2. We're editing an existing entry (initialData exists)
    // 3. Form-specific data has finished loading (formLookupLoading is false)
    // 4. We haven't already updated for this entry and teamspace combination
    if (open && initialData && !formLookupLoading && formTeamspaceId) {
      const entryId = initialData.id;
      const alreadyUpdated = dataLoadedUpdateRef.current.entryId === entryId && 
                            dataLoadedUpdateRef.current.teamspaceId === formTeamspaceId;
      
      if (alreadyUpdated) {
        return; // Already updated for this entry/teamspace combination
      }

      const hasProject = initialData.project && initialData.project !== '' && initialData.project !== null;
      const hasCategory = initialData.category && initialData.category !== '' && initialData.category !== null;
      const hasTag = initialData.tag !== null && initialData.tag !== undefined && 
                     ((Array.isArray(initialData.tag) && initialData.tag.length > 0) || 
                      (!Array.isArray(initialData.tag) && initialData.tag !== ''));

      // Check if project/category/tag options are now available
      const projectOption = hasProject && !hasCategory 
        ? projectAndCategoryOptions.find(option => String(option.id) === String(initialData.project) && !option.isCategory)
        : null;
      const categoryOption = hasCategory && !hasProject
        ? projectAndCategoryOptions.find(option => String(option.id) === String(initialData.category) && option.isCategory)
        : null;
      const tagOptionsAvailable = hasTag && tagOptions.length > 0
        ? tagOptions.filter(option => {
            const tagIds = Array.isArray(initialData.tag) ? initialData.tag : [initialData.tag];
            return tagIds.some(tagId => String(tagId) === String(option.id));
          })
        : [];

      // Only update if we have values that should be set and options are available
      const needsProjectUpdate = hasProject && !hasCategory && projectOption;
      const needsCategoryUpdate = hasCategory && !hasProject && categoryOption;
      const needsTagUpdate = hasTag && tagOptionsAvailable.length > 0;

      if (needsProjectUpdate || needsCategoryUpdate || needsTagUpdate) {
        console.log('🔄 [QuickTimeEntryForm] Updating form data after form-specific data loaded:', {
          entryId: initialData.id,
          teamspaceId: formTeamspaceId,
          needsProjectUpdate,
          needsCategoryUpdate,
          needsTagUpdate,
          projectOption: projectOption?.label,
          categoryOption: categoryOption?.label,
          tagOptionsCount: tagOptionsAvailable.length
        });

        setFormData(prev => {
          const updates = {};
          
          if (needsProjectUpdate) {
            updates.project = String(initialData.project);
            updates.category = ''; // Clear category if setting project
          }
          
          if (needsCategoryUpdate) {
            updates.category = String(initialData.category);
            updates.project = ''; // Clear project if setting category
          }
          
          if (needsTagUpdate) {
            const tagIds = Array.isArray(initialData.tag) 
              ? initialData.tag.map(id => String(id)).filter(id => id !== 'undefined' && id !== 'null')
              : [String(initialData.tag)].filter(id => id !== 'undefined' && id !== 'null');
            updates.tag = tagIds;
          }

          return { ...prev, ...updates };
        });

        // Mark as updated for this entry/teamspace combination
        dataLoadedUpdateRef.current = { entryId, teamspaceId: formTeamspaceId };
      }
    }
  }, [open, initialData, formLookupLoading, formTeamspaceId, projectAndCategoryOptions, tagOptions]);

  // Reset update tracking when form closes or entry changes
  useEffect(() => {
    if (!open) {
      dataLoadedUpdateRef.current = { entryId: null, teamspaceId: null };
    } else if (initialData && dataLoadedUpdateRef.current.entryId !== initialData.id) {
      // Entry changed - reset tracking
      dataLoadedUpdateRef.current = { entryId: null, teamspaceId: null };
    }
  }, [open, initialData?.id]);

  // Check if any fields that will be reset are selected
  const hasFieldsToReset = () => {
    // Check if member is set to current user by email (not by ID, since IDs differ per teamspace)
    let memberIsCurrentUser = false;
    
    const memberArray = Array.isArray(formData.member) ? formData.member : (formData.member ? [formData.member] : []);
    if (memberArray.length > 0 && user) {
      // Check if ALL selected members are the current user
      const allAreCurrentUser = memberArray.every(memberId => {
        // Method 1: Check using formMembers (preferred - checks by email)
        if (formMembers.length > 0) {
          const memberEntry = formMembers.find(m => String(m.id) === String(memberId));
          if (memberEntry) {
            return memberEntry.email === user.email ||
                   memberEntry.email?.toLowerCase() === user.email?.toLowerCase();
          }
        }
        // Method 2: Fallback - check using currentUserMember
        if (currentUserMember) return String(memberId) === String(currentUserMember.id);
        return false;
      });
      memberIsCurrentUser = allAreCurrentUser;
    }

    // Don't count member if it's the current user AND project, category, and tag are empty
    const hasMember = memberArray.length > 0 && !memberIsCurrentUser;
    const hasProject = formData.project && formData.project !== '';
    const hasCategory = formData.category && formData.category !== '';
    const hasTag = formData.tag && Array.isArray(formData.tag) && formData.tag.length > 0;
    
    // Only show confirmation if member is not current user, OR if project/category/tag are set
    return hasMember || hasProject || hasCategory || hasTag;
  };

  // Handle teamspace change with confirmation if needed
  const handleTeamspaceChange = (newTeamspaceId) => {
    const normalized = newTeamspaceId === '' ? null : Number(newTeamspaceId);
    const nextTeamspaceId = normalized === null || Number.isNaN(normalized) ? newTeamspaceId : normalized;
    if (hasFieldsToReset()) {
      setPendingTeamspaceId(nextTeamspaceId);
      setShowTeamspaceChangeDialog(true);
    } else {
      setFormTeamspaceId(nextTeamspaceId);
    }
  };

  // Handle confirmation dialog actions
  const handleConfirmTeamspaceChange = () => {
    if (pendingTeamspaceId) {
      setFormTeamspaceId(pendingTeamspaceId);
      setPendingTeamspaceId(null);
    }
    setShowTeamspaceChangeDialog(false);
  };

  const handleCancelTeamspaceChange = () => {
    setPendingTeamspaceId(null);
    setShowTeamspaceChangeDialog(false);
  };

  // Effect to set member to current user after teamspace change and members are loaded
  useEffect(() => {
    // Check if teamspace changed and members are loaded
    if (open && !formLookupLoading && formMembers.length > 0 && formTeamspaceId && user) {
      // Find current user in the NEW teamspace's members
      const usernameToMatch = user?.username || user?.email;
      const foundMember = formMembers.find(member => 
        member.email === user?.email || 
        member.username === user?.username ||
        member.label === user?.username ||
        member.label === user?.email
      );
      
      if (foundMember && formData.member.length === 0) {
        setFormData(prev => ({
          ...prev,
          member: [String(foundMember.id)]
        }));
      }
    }
  }, [formLookupLoading, formMembers, user, formTeamspaceId, open, formData.member]);

  const handleDelete = async () => {
    if (!initialData || !onDelete) return;
    
    setIsSubmitting(true);
    try {
      if (onDelete) {
        onDelete(initialData.id);
      }
      // Blur before closing to prevent aria-hidden warning
      if (document.activeElement && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      onClose();
    } catch (error) {
      console.error('Error deleting time entry:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail || 
                          'An unexpected error occurred while deleting the time entry.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    } else if (e.key === 'Escape') {
      // Blur before closing to prevent aria-hidden warning
      if (document.activeElement && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      onClose();
    }
  };

  const formatTimeSlot = () => {
    if (initialData) {
      return formatDate(new Date(initialData.start_time), 'MMM d, h:mm a');
    } else if (selectedTimeSlot) {
      return formatDate(selectedTimeSlot.start, 'MMM d, h:mm a');
    }
    return '';
  };

  const isCoordinates = anchorEl && typeof anchorEl.clientX === 'number';
  
  // Convert coordinates to anchorPosition for Popover
  // Position the popover slightly offset from the click to avoid covering the clicked area
  const anchorPosition = isCoordinates 
    ? { 
        top: anchorEl.clientY + 10, // Small offset below click
        left: anchorEl.clientX + 10  // Small offset to the right
      }
    : undefined;

  return (
    <>
    <Popover 
      open={open} 
      onClose={(event, reason) => {
        // Blur any focused element before closing to prevent aria-hidden warning
        if (document.activeElement && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        onClose(event, reason);
      }}
      anchorEl={isCoordinates ? null : anchorEl}
      anchorPosition={anchorPosition}
      anchorOrigin={{
        vertical: isCoordinates ? 'top' : 'bottom',
        horizontal: isCoordinates ? 'left' : 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onMouseDown={(e) => e.stopPropagation()}
      disableRestoreFocus
      // Phones: no anchoring — the paper is pinned full-screen by the sx below.
      {...(isPhone && { anchorReference: 'none' })}
      slotProps={{
        paper: {
          sx: {
            marginTop: isCoordinates ? 0 : '8px', // Add spacing when using element anchor
            ...(isPhone && {
              marginTop: 0,
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
              borderRadius: 0,
            }),
          }
        }
      }}
    >
      <form onSubmit={handleSubmit} style={isPhone ? { height: '100%' } : undefined}>
        <Box sx={{ p: 2.5, width: { xs: '100%', sm: 400 }, height: { xs: '100%', sm: 450 }, maxWidth: { xs: 'none', sm: '90vw' }, maxHeight: { xs: 'none', sm: '90vh' }, overflow: 'auto' }}>
          {/* Soft warning: someone else is editing this entry (Calendar or Time Entries). */}
          <ConcurrentEditNotice grid="time_entries" rowId={open ? initialData?.id : null} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: { xs: 1, sm: 0 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {initialData ? 'Edit Time Entry' : 'New Time Entry'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Teamspace Selector - Disabled when editing existing entry or when loading */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="teamspace-select-label">Teamspace</InputLabel>
                <Select
                  labelId="teamspace-select-label"
                  id="teamspace-select"
                  value={formTeamspaceId || ''}
                  label="Teamspace"
                  onChange={(e) => handleTeamspaceChange(e.target.value)}
                  disabled={!!initialData || formLookupLoading}
                >
                  {teamspaces.map((teamspace) => (
                    <MenuItem key={teamspace.id} value={teamspace.id}>
                      {teamspace.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Delete button - only show for existing entries */}
              {initialData && (
                <IconButton 
                  size="small" 
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={isSubmitting}
                  sx={{ color: theme.palette.error.main, '&:hover': { color: theme.palette.error.dark } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton 
                size="small" 
                onClick={(e) => {
                  // Blur before closing to prevent aria-hidden warning
                  if (document.activeElement && document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                  }
                  onClose(e);
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {successMessage && (
            <div style={{ color: theme.palette.success.main, fontWeight: 600, marginBottom: 8 }}>
              {successMessage}
            </div>
          )}
          
          {error && (
            <Typography variant="body2" color="error" sx={{ mb: 2.5 }}>
              {error}
            </Typography>
          )}

          <DescriptionSuggestField
            autoFocus
            bulbOnly
            margin="dense"
            value={formData.description}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            fullWidth
            variant="standard"
            suggestions={descriptionSuggestions}
            tagSuggestions={tagSuggestions}
            onSelect={(desc) => setFormData(prev => ({ ...prev, description: desc }))}
            sx={{ p:1 }}
          />
          
          {/* Icon buttons arranged horizontally with wrapping */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
            {/* Member, Project, Tag fields container with loading overlay */}
            <Box sx={{ position: 'relative', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            {/* Member Field with Icon */}
            <>
              {formData.member.length === 0 && (
                <IconButton
                  onClick={() => toggleField('member')}
                  sx={{ color: theme.palette.text.primary }}
                >
                  <PersonIcon />
                </IconButton>
              )}
              {formData.member.length > 0 && (
                <Chip
                  icon={<PersonIcon sx={{ color: theme.palette.text.primary }} />}
                  label={getSelectedLabel('member', memberOptions, formData.member) || (currentUserMember?.label || user?.username || user?.email_alt)}
                  onClick={() => canCreateForOthers && !formLookupLoading && toggleField('member')}
                  onDelete={canCreateForOthers && !formLookupLoading ? () => {
                    setFormData(prev => ({ ...prev, member: [] }));
                    setExpandedFields(prev => ({ ...prev, member: false }));
                  } : undefined}
                  variant="outlined"
                  sx={{
                    cursor: canCreateForOthers ? 'pointer' : 'default',
                    borderColor: theme.palette.info.main + '4D',
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.info.main + '1A',
                    opacity: canCreateForOthers ? 1 : 0.7
                  }}
                />
              )}
              {validationErrors.member && (
                <Typography variant="caption" color="error">
                  {validationErrors.member}
                </Typography>
              )}
            </>

            {/* Project Field with Icon */}
            <>
              {!formData.project && !formData.category && (
                <IconButton 
                  onClick={() => toggleField('project')}
                  sx={{ color: theme.palette.text.primary }}
                  disabled={formLookupLoading}
                >
                  <FolderIcon />
                </IconButton>
              )}
              {(formData.project || formData.category) && (
                <Chip 
                  icon={<FolderIcon sx={{ color: theme.palette.text.primary }} />}
                  label={getSelectedLabel('project', availableProjects, formData.project)}
                  onClick={() => !formLookupLoading && toggleField('project')}
                  onDelete={!formLookupLoading ? () => {
                    setFormData(prev => ({ ...prev, project: '', category: '' }));
                    setExpandedFields(prev => ({ ...prev, project: false }));
                    setBillableSetByProject(false);
                    setBillableOverriddenFromProject(false);
                  } : undefined}
                  variant="outlined"
                  sx={{ cursor: formLookupLoading ? 'default' : 'pointer', borderColor: theme.palette.info.main + '4D', color: theme.palette.text.primary, backgroundColor: theme.palette.info.main + '1A' }}
                />
              )}
              {validationErrors.project && (
                <Typography variant="caption" color="error">
                  {validationErrors.project}
                </Typography>
              )}
            </>

            {/* Tag Field with Icon */}
            <>
              {(!formData.tag || (Array.isArray(formData.tag) && formData.tag.length === 0)) && (
                <IconButton 
                  onClick={() => toggleField('tag')}
                  sx={{ color: theme.palette.text.primary }}
                  disabled={formLookupLoading}
                >
                  <LocalOfferIcon />
                </IconButton>
              )}
              {formData.tag && Array.isArray(formData.tag) && formData.tag.length > 0 && (
                <Chip 
                  icon={<LocalOfferIcon sx={{ color: theme.palette.text.primary }} />}
                  label={getSelectedLabel('tag', tagOptions, formData.tag)}
                  onClick={() => !formLookupLoading && toggleField('tag')}
                  onDelete={!formLookupLoading ? () => {
                    setFormData(prev => ({
                      ...prev,
                      tag: [],
                      description: syncDescriptionWithTags({
                        description: prev.description,
                        previousTagIds: prev.tag,
                        nextTagIds: [],
                        tagLookup: formTagLookup,
                      }),
                    }));
                    setExpandedFields(prev => ({ ...prev, tag: false }));
                  } : undefined}
                  variant="outlined"
                  sx={{ cursor: formLookupLoading ? 'default' : 'pointer', borderColor: theme.palette.info.main + '4D', color: theme.palette.text.primary, backgroundColor: theme.palette.info.main + '1A' }}
                />
              )}
              {validationErrors.tag && (
                <Typography variant="caption" color="error">
                  {validationErrors.tag}
                </Typography>
              )}
            </>
            {/* Loading overlay for member, project, and tag fields */}
            {formLookupLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  zIndex: 1000,
                  borderRadius: 1,
                }}
              >
                <CircularProgress size={40} />
              </Box>
            )}
            </Box>

            {/* Billable Field with Icon */}
            <>
              <Tooltip title={formData.category ? 'Categories are non-billable' : ''}>
                <span>
                  <IconButton
                    disabled={Boolean(formData.category)}
                    onClick={() => {
                      if (billableSetByProject) setBillableOverriddenFromProject(true);
                      setFormData(prev => ({ ...prev, billable: !prev.billable }));
                      setBillableSetByProject(false);
                    }}
                    sx={{
                      color: (formData.billable && !formData.category) ? '#FFD700' : theme.palette.text.primary,
                      ...(billableSetByProject && {
                        boxShadow: '0 0 0 2px #f44336',
                      }),
                    }}
                    size="small"
                  >
                    <Typography sx={{
                      fontSize: '1rem',
                      fontWeight: (formData.billable && !formData.category) ? 'bold' : 'normal',
                    }}>
                      $
                    </Typography>
                  </IconButton>
                </span>
              </Tooltip>
              {billableOverriddenFromProject && (
                <Typography variant="caption" sx={{ color: 'warning.main' }}>
                  Billable changed from project default
                </Typography>
              )}
            </>
          </Box>

          {/* Expandable Autocomplete fields */}
          {expandedFields.member && (
            <Autocomplete
              multiple
              required
              disabled={memberLocked}
              disablePortal
              options={memberOptions}
              loading={formLookupLoading}
              open
              onClose={(event, reason) => {
                if (reason === 'blur' || reason === 'escape') {
                  setExpandedFields(prev => ({ ...prev, member: false }));
                }
              }}
              value={memberOptions.filter(option => formData.member.includes(String(option.id)))}
              onChange={memberLocked ? undefined : handleAutocompleteChange('member')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              filterOptions={(options, state) =>
                options.filter(option => {
                  const l = (option && option.label) ? String(option.label) : '';
                  const q = (state && state.inputValue) ? String(state.inputValue) : '';
                  return l.toLowerCase().includes(q.toLowerCase());
                })
              }
              sx={{ width: '100%', mb: 1 }}
              renderInput={(params) => <TextField {...params} label="Member" autoFocus disabled={isStaff}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                  },
                  '& fieldset': {
                    borderColor: theme.palette.info.main + '4D',
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.info.main + '80',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.info.main,
                  },
                }} />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.label}
                    size="small"
                  />
                ))
              }
            />
          )}
          {expandedFields.project && (
            <Autocomplete
              disablePortal
              options={projectAndCategoryOptions}
              loading={formLookupLoading}
              open
              onClose={() => setExpandedFields(prev => ({ ...prev, project: false }))}
              value={
                formData.category 
                  ? projectAndCategoryOptions.find(option => option.id === formData.category && option.isCategory) || null
                  : (formData.project 
                      ? projectAndCategoryOptions.find(option => option.id === formData.project && !option.isCategory) || null
                      : null)
              }
              onChange={handleAutocompleteChange('project')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, state) =>
                options.filter(option => {
                  const l = (option && option.label) ? String(option.label) : '';
                  const q = (state && state.inputValue) ? String(state.inputValue) : '';
                  return l.toLowerCase().includes(q.toLowerCase());
                })
              }
              sx={{ width: '100%', mb: 1 }}
              renderInput={(params) => <TextField {...params} label="Project" autoFocus />}
            />
          )}
          {expandedFields.tag && (
            <Autocomplete
              multiple
              disablePortal
              options={tagOptions}
              loading={formLookupLoading}
              open
              onClose={(event, reason) => {
                if (reason === 'blur' || reason === 'escape') {
                  setExpandedFields(prev => ({ ...prev, tag: false }));
                }
              }}
              value={Array.isArray(formData.tag) && formData.tag.length > 0
                ? tagOptions.filter(option => {
                    // Convert both to strings for comparison to handle type mismatches
                    return formData.tag.some(tagId => String(tagId) === String(option.id));
                  })
                : []}
              onChange={handleAutocompleteChange('tag')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, state) =>
                options.filter(option => {
                  const l = (option && option.label) ? String(option.label) : '';
                  const q = (state && state.inputValue) ? String(state.inputValue) : '';
                  return l.toLowerCase().includes(q.toLowerCase());
                })
              }
              sx={{ width: '100%', mb: 1 }}
              renderInput={(params) => <TextField {...params} label="Tags (optional)" autoFocus />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.label}
                    size="small"
                  />
                ))
              }
            />
          )}
          
          {/* Date and Time Fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Date"
                type="date"
                value={formData.date ? formData.date.format('YYYY-MM-DD') : ''}
                onChange={(e) => {
                  const newDate = dayjs(e.target.value);
                  if (newDate.isValid()) {
                    setFormData(prev => {
                      const updated = { ...prev, date: newDate };
                      // If end_time exists and start_time exists, update end_time to keep same duration
                      if (updated.end_time && updated.end_time.isValid() && updated.start_time && updated.start_time.isValid()) {
                        const oldStartDateTime = prev.date
                          .hour(prev.start_time.hour())
                          .minute(prev.start_time.minute())
                          .second(0);
                        const oldEndDateTime = prev.end_time;
                        const durationMinutes = oldEndDateTime.diff(oldStartDateTime, 'minute');
                        
                        const newStartDateTime = newDate
                          .hour(updated.start_time.hour())
                          .minute(updated.start_time.minute())
                          .second(0);
                        updated.end_time = newStartDateTime.add(durationMinutes, 'minute');
                      }
                      return updated;
                    });
                  }
                }}
                InputLabelProps={{ shrink: true }}
                margin="dense"
                error={!!validationErrors.date}
                helperText={validationErrors.date}
                sx={{ flex: 2 }}
              />
              {/* Timezone Field - Beside date picker */}
              <TextField
                label="Timezone"
                value={formData.timezone || displayTimezone || 'UTC'}
                InputProps={{
                  readOnly: true,
                }}
                margin="dense"
                variant="outlined"
                sx={{ 
                  flex: 1,
                  '& .MuiInputBase-input': {
                    color: theme.palette.text.secondary,
                    cursor: 'default',
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.info.main + '40',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.info.main + '80',
                  },
                  '& .MuiOutlinedInput-root.Mui-disabled': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.info.main + '40',
                    },
                  },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileTimePicker 
                  label="Start Time"
                  value={formData.start_time}
                  onChange={handleDateTimeChange('start_time')}
                  ampm
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: 'dense',
                      required: true,
                      error: !!validationErrors.start_time,
                      helperText: validationErrors.start_time,
                      inputProps: { 
                        inputMode: 'numeric',
                        pattern: '[0-9: ]*',
                        placeholder: 'HH:MM AM/PM'
                      },
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: theme.palette.background.paper,
                          color: theme.palette.text.primary,
                        },
                        '& .MuiInputLabel-root': {
                          color: theme.palette.text.secondary,
                        },
                        '& fieldset': {
                          borderColor: theme.palette.info.main + '4D',
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.info.main + '80',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.info.main,
                        },
                      }
                    },
                    actionBar: {
                      actions: ['cancel', 'accept'],
                      sx: {
                        '& .MuiButtonBase-root': {
                          color: theme.palette.text.primary,
                        }
                      }
                    }
                  }}
                  sx={{ flex: 1 }}
                />
                <MobileTimePicker 
                  label="End Time (optional)"
                  value={formData.end_time}
                  onChange={handleDateTimeChange('end_time')}
                  ampm
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: 'dense',
                      error: !!validationErrors.end_time,
                      inputProps: { 
                        inputMode: 'numeric',
                        pattern: '[0-9: ]*',
                        placeholder: 'HH:MM AM/PM'
                      },
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: theme.palette.background.paper,
                          color: theme.palette.text.primary,
                        },
                        '& .MuiInputLabel-root': {
                          color: theme.palette.text.secondary,
                        },
                        '& fieldset': {
                          borderColor: theme.palette.info.main + '4D',
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.info.main + '80',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.info.main,
                        },
                      }
                    },
                    actionBar: {
                      actions: ['cancel', 'accept'],
                      sx: {
                        '& .MuiButtonBase-root': {
                          color: theme.palette.text.primary,
                        }
                      }
                    }
                  }}
                  sx={{ flex: 1 }}
                />
              </LocalizationProvider>
            </Box>
            {formData.start_time && formData.start_time.isValid() && 
             formData.end_time && formData.end_time.isValid() && 
             (() => {
               const startDateTime = formData.date
                 .hour(formData.start_time.hour())
                 .minute(formData.start_time.minute())
                 .second(0);
               const endDateTime = formData.date
                 .hour(formData.end_time.hour())
                 .minute(formData.end_time.minute())
                 .second(0);
               return endDateTime.isBefore(startDateTime);
             })() && (
              <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, ml: 1.75 }}>
                This will span to the next day
              </Typography>
            )}
          </Box>
          
          {/* Duration (Editable, auto-calculated from start and end time) */}
          <Box sx={{ mt: 2, mb: 2, px: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Typography sx={{ flexGrow: 1, pt: 1 }}>
                Duration (HH:MM) (optional)
              </Typography>
              <TextField
                type="text"
                inputRef={durationInputRef}
                value={durationInputValue !== null ? durationInputValue : minutesToHHMM(formData.duration_minutes)}
                onChange={(e) => {
                  // Handle all input including deletion/backspace
                  const inputValue = e.target.value;
                  const input = e.target;
                  const cursorPosition = input.selectionStart;
                  
                  // Always update the display value to allow deletion
                  setDurationInputValue(inputValue);
                  durationPrevValueRef.current = inputValue;
                  
                  // Check if hours > 24
                  const hours = getHoursFromHHMM(inputValue);
                  if (inputValue && inputValue.includes(':') && hours > 24) {
                    setDurationError('Hours cannot exceed 24');
                    // Don't update formData if hours > 24, just keep the input value
                  } else {
                    // Check if total minutes > 1440 (24 hours)
                    if (inputValue && inputValue.includes(':')) {
                      const minutes = HHMMToMinutes(inputValue);
                      if (minutes > 1440) {
                        setDurationError('Duration cannot exceed 24 hours (1440 minutes)');
                        // Don't update formData if minutes > 1440, just keep the input value
                      } else {
                        setDurationError('');
                        // Update formData only if within valid range
                        const clampedValue = Math.min(Math.max(minutes, 0), 1440);
                        setFormData(prev => {
                          const updated = { ...prev, duration_minutes: clampedValue };
                          // If start_time exists, calculate end_time
                          if (updated.start_time && updated.start_time.isValid() && clampedValue > 0) {
                            const startDateTime = updated.date
                              .hour(updated.start_time.hour())
                              .minute(updated.start_time.minute())
                              .second(0);
                            updated.end_time = startDateTime.add(clampedValue, 'minute');
                          } else {
                            updated.end_time = null;
                          }
                          return updated;
                        });
                      }
                    } else {
                      setDurationError('');
                    }
                  }
                  
                  // Preserve cursor position after React updates
                  setTimeout(() => {
                    if (durationInputRef.current) {
                      // Use the exact cursor position from the input event
                      const newPosition = Math.min(cursorPosition, inputValue.length);
                      durationInputRef.current.setSelectionRange(newPosition, newPosition);
                    }
                  }, 0);
                }}
                onBlur={(e) => {
                  const inputValue = e.target.value.trim();
                  const hours = getHoursFromHHMM(inputValue);
                  
                  // Check if hours > 24 before processing
                  if (inputValue && inputValue.includes(':') && hours > 24) {
                    setDurationError('Hours cannot exceed 24');
                    // Don't normalize if there's an error, keep the input as-is
                    return;
                  }
                  
                  // Check if total minutes > 1440 before processing
                  if (inputValue && inputValue.includes(':')) {
                    const minutes = HHMMToMinutes(inputValue);
                    if (minutes > 1440) {
                      setDurationError('Duration cannot exceed 24 hours (1440 minutes)');
                      // Don't normalize if there's an error, keep the input as-is
                      return;
                    }
                  }
                  
                  setDurationError('');
                  
                  if (inputValue === '') {
                    // If empty on blur, default to 15 minutes (00:06)
                    const defaultMinutes = 15;
                    setDurationInputValue(null);
                    setFormData(prev => {
                      const updated = { ...prev, duration_minutes: defaultMinutes };
                      // If start_time exists, calculate end_time
                      if (updated.start_time && updated.start_time.isValid()) {
                        const startDateTime = updated.date
                          .hour(updated.start_time.hour())
                          .minute(updated.start_time.minute())
                          .second(0);
                        updated.end_time = startDateTime.add(defaultMinutes, 'minute');
                      }
                      return updated;
                    });
                  } else {
                    // Normalize on blur: convert any MM value to proper HH:MM
                    // e.g., 01:80 becomes 02:20 automatically
                    const minutes = HHMMToMinutes(inputValue);
                  const clampedValue = Math.min(Math.max(minutes, 0), 1440); // 24 hours max
                    const normalizedValue = minutesToHHMM(clampedValue);
                    setDurationInputValue(null);
                    handleDurationChange(normalizedValue);
                  }
                }}
                placeholder="HH:MM"
                inputProps={{ 
                  pattern: '[0-9]{1,2}:[0-9]{1,}',
                  inputMode: 'numeric'
                }}
                size="small"
                error={!!validationErrors.duration_minutes || !!durationError}
                sx={{ width: 120 }}
              />
            </Box>
            {(durationError || validationErrors.duration_minutes) && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                <Box>
                  {durationError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {durationError}
                    </Typography>
                  )}
                  {validationErrors.duration_minutes && !durationError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap' }}>
                {validationErrors.duration_minutes}
              </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* Notes Field and audit info */}
          <Box sx={{ mt: 1, mb: 1, px: 1 }}>
            <TextField
              label="Notes (optional)"
              name="notes"
              value={formData.notes || ''}
              onChange={handleChange}
              multiline
              minRows={3}
              fullWidth
              inputProps={{ maxLength: 100 }}
              helperText={`${(formData.notes || '').length}/100`}
              sx={{
                '& .MuiInputBase-input': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.secondary,
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.background.paper,
                },
                '& fieldset': {
                  borderColor: theme.palette.info.main + '4D',
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.info.main + '80',
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.info.main,
                },
                '& .MuiFormHelperText-root': {
                  color: theme.palette.text.secondary,
                  textAlign: 'right',
                },
              }}
            />
          </Box>
          {isEdit && (
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Typography sx={{ fontSize: '0.68rem', color: theme.palette.text.disabled }}>
                Created by: <span style={{ fontWeight: 500 }}>{initialData?.created_by ? (formMemberLookup[String(initialData.created_by)] || `#${initialData.created_by}`) : '—'}</span>
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: theme.palette.text.disabled }}>
                Last edited by: <span style={{ fontWeight: 500 }}>{initialData?.lastedited_by ? (formMemberLookup[String(initialData.lastedited_by)] || `#${initialData.lastedited_by}`) : '—'}</span>
              </Typography>
            </Box>
          )}
        </Box>
        {initialData && (() => {
          const primaryId = String(initialData.member || '');
          const existingMemberIds = isGroupEntry ? groupEntries.map(e => String(e.member)) : [primaryId];
          const currentMemberIds = formData.member.map(String);
          const addedNames = currentMemberIds.filter(id => !existingMemberIds.includes(id)).map(id => formMemberLookup[id] || `Member #${id}`);
          const removedNames = isGroupEntry
            ? existingMemberIds.filter(id => !currentMemberIds.includes(id)).map(id => formMemberLookup[id] || `Member #${id}`)
            : [];
          if (addedNames.length === 0 && removedNames.length === 0) return null;
          return (
            <Box sx={{ px: 2.5, pb: 1, textAlign: 'right' }}>
              {addedNames.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block', color: theme.palette.success.main }}>
                  {addedNames.length} time {addedNames.length === 1 ? 'entry' : 'entries'} will be created for{' '}
                  {addedNames.map((n, i) => <span key={i}><strong>{n}</strong>{i < addedNames.length - 1 ? ', ' : ''}</span>)}
                </Typography>
              )}
              {removedNames.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block', color: theme.palette.warning.main }}>
                  {removedNames.length} time {removedNames.length === 1 ? 'entry' : 'entries'} will be removed for{' '}
                  {removedNames.map((n, i) => <span key={i}><strong>{n}</strong>{i < removedNames.length - 1 ? ', ' : ''}</span>)}
                </Typography>
              )}
            </Box>
          );
        })()}
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', p: 2.5, pt: 0 }}>
          <Button
            onClick={(e) => {
              // Blur before closing to prevent aria-hidden warning
              if (document.activeElement && document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
              }
              onClose(e);
            }} 
            variant="contained" 
            sx={{ bgcolor: theme.palette.error.main, color: theme.palette.error.contrastText, '&:hover': { bgcolor: theme.palette.error.dark } }}
          >
            Cancel
          </Button>
          {isGroupEntry ? (
            <>
              <Button
                onClick={() => { if (validateForm()) setGroupConfirmMode('myself'); }}
                variant="contained"
                sx={{ bgcolor: theme.palette.info.main, color: theme.palette.info.contrastText, '&:hover': { bgcolor: theme.palette.info.dark } }}
              >
                Save for this one only
              </Button>
              <Button
                onClick={() => { if (validateForm()) setGroupConfirmMode('all'); }}
                variant="contained"
                sx={{ bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText, '&:hover': { bgcolor: theme.palette.success.dark } }}
              >
                Save for all members
              </Button>
            </>
          ) : (
            <Button type='submit' variant="contained" sx={{ bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText, '&:hover': { bgcolor: theme.palette.success.dark } }}>Save</Button>
          )}
        </Box>
      </form>
    </Popover>

    {/* Delete Confirmation Dialog */}
    <Dialog
      open={deleteDialogOpen}
      onClose={() => setDeleteDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        Confirm Deletion
      </DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to delete this time entry? This action cannot be undone.
        </Typography>
        {initialData && (
          <Box sx={{ mt: 2, p: 2, bgcolor: theme.palette.background.paper, borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {initialData.description || 'No description'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(new Date(initialData.start_time), 'MMM d, yyyy h:mm a')}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          sx={{bgcolor: theme.palette.primary.light}}
          onClick={() => setDeleteDialogOpen(false)} 
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleDelete}
          disabled={isSubmitting}
          variant="contained"
          sx={{ 
            bgcolor: theme.palette.error.main, 
            '&:hover': { bgcolor: theme.palette.error.dark }
          }}
        >
          {isSubmitting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Confirmation dialog for teamspace change */}
    <Dialog open={showTeamspaceChangeDialog} onClose={handleCancelTeamspaceChange}>
      <DialogTitle>Confirm Teamspace Change</DialogTitle>
      <DialogContent>
        <Typography>
          Changing the teamspace will reset the Member, Project, Category, and Tag fields. 
          All other fields (description, billable, date, time, duration) will be preserved.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelTeamspaceChange} variant="contained" sx={{ bgcolor: '#FF5252', color: '#fff', '&:hover': { bgcolor: '#FF1744' } }}>
          Cancel
        </Button>
        <Button onClick={handleConfirmTeamspaceChange} variant="contained" sx={{ bgcolor: '#4CAF50', color: '#fff', '&:hover': { bgcolor: '#45A049' } }}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>

    {/* Error dialog for API/network failures - form stays open, user can retry or reload */}
    <Dialog open={apiErrorDialogOpen} onClose={() => setApiErrorDialogOpen(false)}>
      <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>
        Error Saving Time Entry
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
        <Typography sx={{ color: theme.palette.text.primary, mb: 2 }}>
          There was an issue saving the time entry. Your changes were not saved.
        </Typography>
        <Typography sx={{ color: theme.palette.text.primary }}>
          Please check your connection and try again, or reload the page if the problem persists.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
        <Button
          onClick={() => window.location.reload()}
          variant="contained"
          sx={{ bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, '&:hover': { bgcolor: theme.palette.primary.dark } }}
        >
          Reload Page
        </Button>
        <Button
          onClick={() => setApiErrorDialogOpen(false)}
          variant="outlined"
          sx={{ color: theme.palette.text.primary, borderColor: theme.palette.info.main, '&:hover': { borderColor: theme.palette.info.dark } }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {/* Duplicate check dialog */}
    <Dialog open={duplicateCheckDialogOpen} onClose={() => setDuplicateCheckDialogOpen(false)}>
      <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>
        Duplicate Entries Detected
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
        <Typography sx={{ color: theme.palette.text.primary, mb: 1 }}>
          The following members already have a time entry for this period:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {duplicateCheckInfo?.duplicateIds.map(memberId => (
            <Chip
              key={memberId}
              label={memberOptions.find(o => String(o.id) === String(memberId))?.label || memberId}
              size="small"
              variant="outlined"
              sx={{ borderColor: theme.palette.warning.main, color: theme.palette.warning.main }}
            />
          ))}
        </Box>
        <Typography sx={{ color: theme.palette.text.secondary }}>
          How would you like to proceed?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
        <Button onClick={() => setDuplicateCheckDialogOpen(false)} sx={{ color: theme.palette.text.primary }}>
          Cancel
        </Button>
        {duplicateCheckInfo && (() => {
          const nonDupes = duplicateCheckInfo.memberIds.filter(id => !duplicateCheckInfo.duplicateIds.includes(id));
          // For edit mode, primary member is always included; only show Skip if there are non-dupe additionals
          const showSkip = duplicateCheckInfo.isEdit
            ? nonDupes.filter(id => id !== duplicateCheckInfo.memberIds[0]).length > 0 || nonDupes.length > 0
            : nonDupes.length > 0;
          return showSkip ? (
            <Button
              onClick={() => {
                setDuplicateCheckDialogOpen(false);
                const skipIds = duplicateCheckInfo.isEdit
                  ? [duplicateCheckInfo.memberIds[0], ...duplicateCheckInfo.memberIds.slice(1).filter(id => !duplicateCheckInfo.duplicateIds.includes(id))]
                  : nonDupes;
                doPerformSave(skipIds, duplicateCheckInfo.basePayload, duplicateCheckInfo.isEdit);
              }}
              variant="outlined"
              sx={{ color: theme.palette.text.primary, borderColor: theme.palette.info.main }}
            >
              Skip Duplicates
            </Button>
          ) : null;
        })()}
        <Button
          onClick={() => {
            setDuplicateCheckDialogOpen(false);
            doPerformSave(duplicateCheckInfo.memberIds, duplicateCheckInfo.basePayload, duplicateCheckInfo.isEdit);
          }}
          variant="contained"
          sx={{ bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText, '&:hover': { bgcolor: theme.palette.success.dark } }}
        >
          Create All Anyway
        </Button>
      </DialogActions>
    </Dialog>

    {/* Group scope confirmation dialog */}
    <Dialog open={!!groupConfirmMode} onClose={() => setGroupConfirmMode(null)} maxWidth="xs" fullWidth>
      {groupConfirmMode === 'myself' && (() => {
        const existingMemberIds = groupEntries.map(e => String(e.member));
        const currentMemberIds = formData.member.map(String);
        const otherMembers = groupEntries
          .filter(e => String(e.member) !== String(initialData?.member))
          .map(e => formMemberLookup[String(e.member)] || `Member #${e.member}`);
        const addedNames = currentMemberIds.filter(id => !existingMemberIds.includes(id)).map(id => formMemberLookup[id] || `Member #${id}`);
        const removedNames = existingMemberIds.filter(id => !currentMemberIds.includes(id)).map(id => formMemberLookup[id] || `Member #${id}`);
        const boldList = (names) => names.map((n, i) => <span key={i}><strong>{n}</strong>{i < names.length - 1 ? ', ' : ''}</span>);
        return (
          <>
            <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>
              Confirm "Save for This One Only"
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
              <Typography sx={{ color: theme.palette.text.primary }}>
                This will save changes only to this entry and remove it from the group.
                You will no longer be able to view or edit this entry alongside{' '}
                {otherMembers.length === 1
                  ? <strong>{otherMembers[0]}</strong>
                  : <>{otherMembers.slice(0, -1).map((n, i) => <span key={i}><strong>{n}</strong>, </span>)}<strong>{otherMembers[otherMembers.length - 1]}</strong></>
                }'s {otherMembers.length === 1 ? 'entry' : 'entries'}.
              </Typography>
              {addedNames.length > 0 && (
                <Typography sx={{ color: theme.palette.success.main, mt: 1 }}>
                  {addedNames.length} time {addedNames.length === 1 ? 'entry' : 'entries'} will be created for {boldList(addedNames)}
                </Typography>
              )}
              {removedNames.length > 0 && (
                <Typography sx={{ color: theme.palette.warning.main, mt: 1 }}>
                  {removedNames.length} time {removedNames.length === 1 ? 'entry' : 'entries'} will be removed for {boldList(removedNames)}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
              <Button onClick={() => setGroupConfirmMode(null)} sx={{ color: theme.palette.text.primary }}>Cancel</Button>
              <Button
                onClick={() => { setGroupConfirmMode(null); performSubmit('myself'); }}
                variant="contained"
                sx={{ bgcolor: theme.palette.info.main, color: theme.palette.info.contrastText, '&:hover': { bgcolor: theme.palette.info.dark } }}
              >
                Save for This One Only
              </Button>
            </DialogActions>
          </>
        );
      })()}
      {groupConfirmMode === 'all' && (() => {
        const existingMemberIds = groupEntries.map(e => String(e.member));
        const currentMemberIds = formData.member.map(String);
        const retainedNames = currentMemberIds.filter(id => existingMemberIds.includes(id)).map(id => formMemberLookup[id] || `Member #${id}`);
        const addedNames = currentMemberIds.filter(id => !existingMemberIds.includes(id)).map(id => formMemberLookup[id] || `Member #${id}`);
        const removedNames = existingMemberIds.filter(id => !currentMemberIds.includes(id)).map(id => formMemberLookup[id] || `Member #${id}`);
        const boldList = (names) => names.map((n, i) => <span key={i}><strong>{n}</strong>{i < names.length - 1 ? ', ' : ''}</span>);
        return (
          <>
            <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>
              Confirm "Save for All Members"
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
              {retainedNames.length > 0 && (
                <Typography sx={{ color: theme.palette.text.primary, mb: 1 }}>
                  Changes will be applied to: {boldList(retainedNames)}
                </Typography>
              )}
              {addedNames.length > 0 && (
                <Typography sx={{ color: theme.palette.success.main, mt: 1 }}>
                  {addedNames.length} time {addedNames.length === 1 ? 'entry' : 'entries'} will be created for {boldList(addedNames)}
                </Typography>
              )}
              {removedNames.length > 0 && (
                <Typography sx={{ color: theme.palette.warning.main, mt: 1 }}>
                  {removedNames.length} time {removedNames.length === 1 ? 'entry' : 'entries'} will be removed for {boldList(removedNames)}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
              <Button onClick={() => setGroupConfirmMode(null)} sx={{ color: theme.palette.text.primary }}>Cancel</Button>
              <Button
                onClick={() => { setGroupConfirmMode(null); performSubmit('all'); }}
                variant="contained"
                sx={{ bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText, '&:hover': { bgcolor: theme.palette.success.dark } }}
              >
                Save for All Members
              </Button>
            </DialogActions>
          </>
        );
      })()}
    </Dialog>
    </>
  );
};

export default QuickTimeEntryForm;